import { Resolver, useForm } from "react-hook-form";
import { AssetType, BucketDataType, UpdateAssetDataType } from "../types/bucketTypes";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type AssetOverviewFormData = UpdateAssetDataType & {
    last_scan?: string | null;
}


export const useAssetOverviewForm = (asset?: AssetType, bucket?: BucketDataType) => {
    const { t, i18n } = useTranslation();
    const escoAssetValidation = useMemo(() => {
        return yup.object({
            asset_id: yup.number().required().label('Asset ID'),
            machine_number: yup.string().nullable().trim().label('Machine number'),
            stock_number: yup.string().nullable().trim().label('Stock number'),
            attachment_type: yup.string().optional().nullable().label('Attachment type'),
            make: yup.string().optional().nullable().label('Make'),
            equipment_model: yup.string().optional().nullable().label('Equipment model'),
            class: yup.string().optional().nullable().trim().label('Class'),
            capacity: yup.string().optional().nullable().label('Capacity'),
            lip_width: yup.number().transform((value) => (isNaN(value) ? null : value)).optional().nullable().label('Lip width'),
            manufacturer: yup.string().optional().nullable().label('Manufacturer'),
            last_scan: yup.string().optional().nullable().label('Last scan'),
        });
    }, [i18n.language, t])

    const nonEscoAssetValidation = useMemo(() => {
        return yup.object({
            asset_id: yup.number().required().label('Asset ID'),
            machine_number: yup.string().nullable().trim().label('Machine number'),
            stock_number: yup.string().nullable().trim().label('Stock number'),
            attachment_type: yup.string().required().min(2).trim().label('Attachment type'),
            make: yup.string().required().min(2).trim().label('Make'),
            equipment_model: yup.string().required().min(2).trim().label('Equipment model'),
            class: yup.string().optional().nullable().trim().label('Class'),
            capacity: yup.string().optional().nullable().label('Capacity'),
            lip_width: yup.number().min(0).typeError(t('validation.lipWidthNumber')).transform((value, originalValue) => {
                return originalValue == '' ? null : value;
            }).optional().nullable().label('Lip width'),
            manufacturer: yup.string().required().min(2).trim().test('is-valid-manufacturer', t('validation.manufacturerNotValid'), value => {
                return value.trim().toLowerCase() !== 'esco';
            }).label('Manufacturer'),
            last_scan: yup.string().optional().nullable().label('Last scan'),
        })
    }, [i18n.language, t]);
    const validation = asset?.bucket?.bucket_type?.toLowerCase() === 'esco' ? escoAssetValidation : nonEscoAssetValidation;
    const assetDefaultValues: Partial<AssetOverviewFormData> = {
        asset_id: asset?.bucket?.asset_id,
        stock_number: asset?.bucket?.stock_number ?? "",
        attachment_type: asset?.bucket?.attachment_type ?? "",
        equipment_model: asset?.bucket?.model ?? "",
        capacity: asset?.bucket?.capacity ?? "",
        manufacturer: asset?.bucket?.manufacturer ?? "",
        machine_number: asset?.bucket?.machine_number ?? "",
        make: asset?.bucket?.make ?? "",
        class: asset?.bucket?.class ?? "",
        lip_width: asset?.bucket?.lip_width ?? undefined,
        last_scan: "",
    }
    const bucketDefaultValues: Partial<AssetOverviewFormData> = {
        asset_id: bucket?.asset_id,
        stock_number: bucket?.stock_number ?? "",
        attachment_type: bucket?.attachment_type ?? "",
        equipment_model: bucket?.model ?? "",
        capacity: bucket?.capacity ?? "",
        manufacturer: bucket?.manufacturer ?? "",
        machine_number: bucket?.machine_number ?? "",
        make: bucket?.make ?? "",
        class: bucket?.class ?? "",
        lip_width: bucket?.lip_width ?? undefined,
        last_scan: "",
    }
    return useForm<AssetOverviewFormData, unknown, AssetOverviewFormData>({
        defaultValues: asset ? assetDefaultValues : bucketDefaultValues,
        mode: "onChange",
        resolver: yupResolver(validation) as unknown as Resolver<AssetOverviewFormData, unknown, AssetOverviewFormData>
    });
}